import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '../../i18n/LanguageContext';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Stack,
  MenuItem,
  Divider,
} from '@mui/material';
import { Save, Cancel } from '@mui/icons-material';
import { employeeService } from '../../services/employee.service';
import { Employee, Department } from '../../types';

const EmployeeForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [departments, setDepartments] = useState<Department[]>([]);

  const [employee, setEmployee] = useState<Partial<Employee>>({
    employeeNumber: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    departmentId: 0,
    position: '',
    salary: 0,
    employmentType: 'FULL_TIME',
    status: 'ACTIVE',
    address: '',
    city: '',
    country: '',
  });

  useEffect(() => {
    loadDepartments();
    if (id) {
      loadEmployee();
    }
  }, [id]);

  const loadDepartments = async () => {
    try {
      const data = await employeeService.getDepartments();
      setDepartments(data);
    } catch (error) {
      console.error('Error loading departments:', error);
    }
  };

  const loadEmployee = async () => {
    try {
      const data = await employeeService.getById(Number(id));
      setEmployee({
        ...data,
        dateOfBirth: data.dateOfBirth ? data.dateOfBirth.split('T')[0] : '',
      });
    } catch (error) {
      console.error('Error loading employee:', error);
      setError('Failed to load employee');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!employee.employeeNumber) {
      setError('Employee number is required');
      setLoading(false);
      return;
    }

    if (!employee.firstName || !employee.lastName) {
      setError('First name and last name are required');
      setLoading(false);
      return;
    }

    if (!employee.departmentId || employee.departmentId === 0) {
      setError('Please select a department');
      setLoading(false);
      return;
    }

    try {
      if (id) {
        await employeeService.update(Number(id), employee);
      } else {
        await employeeService.create(employee);
      }
      navigate('/employees');
    } catch (err: any) {
      console.error('Error saving employee:', err);
      setError(err.response?.data?.message || 'Failed to save employee');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {id ? t('employees.edit') : t('employees.new')}
      </Typography>

      <Paper sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={3}>
            {/* Personal Information */}
            <Typography variant="h6" color="text.secondary">
              Personal Information
            </Typography>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label={t('employees.employee_number')}
                value={employee.employeeNumber}
                onChange={(e) => setEmployee({ ...employee, employeeNumber: e.target.value })}
                disabled={loading || !!id}
                required
                placeholder="e.g. EMP001"
              />
              <TextField
                fullWidth
                label={t('employees.first_name')}
                value={employee.firstName}
                onChange={(e) => setEmployee({ ...employee, firstName: e.target.value })}
                disabled={loading}
                required
              />
              <TextField
                fullWidth
                label={t('employees.last_name')}
                value={employee.lastName}
                onChange={(e) => setEmployee({ ...employee, lastName: e.target.value })}
                disabled={loading}
                required
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label={t('common.email')}
                type="email"
                value={employee.email}
                onChange={(e) => setEmployee({ ...employee, email: e.target.value })}
                disabled={loading}
                required
              />
              <TextField
                fullWidth
                label={t('common.phone')}
                value={employee.phone}
                onChange={(e) => setEmployee({ ...employee, phone: e.target.value })}
                disabled={loading}
              />
              <TextField
                fullWidth
                type="date"
                label={t('employees.date_of_birth')}
                value={employee.dateOfBirth || ''}
                onChange={(e) => setEmployee({ ...employee, dateOfBirth: e.target.value })}
                disabled={loading}
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            <Divider />

            {/* Employment Details */}
            <Typography variant="h6" color="text.secondary">
              Employment Details
            </Typography>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                select
                label={t('employees.department')}
                value={employee.departmentId || ''}
                onChange={(e) =>
                  setEmployee({ ...employee, departmentId: Number(e.target.value) })
                }
                disabled={loading}
                required
              >
                <MenuItem value={0} disabled>
                  Select a department
                </MenuItem>
                {departments.map((dept) => (
                  <MenuItem key={dept.id} value={dept.id}>
                    {dept.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                label={t('employees.position')}
                value={employee.position}
                onChange={(e) => setEmployee({ ...employee, position: e.target.value })}
                disabled={loading}
                required
                placeholder="e.g. Software Developer"
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                select
                label={t('employees.employment_type')}
                value={employee.employmentType}
                onChange={(e) => setEmployee({ ...employee, employmentType: e.target.value })}
                disabled={loading}
              >
                <MenuItem value="FULL_TIME">{t('employees.full_time')}</MenuItem>
                <MenuItem value="PART_TIME">{t('employees.part_time')}</MenuItem>
                <MenuItem value="CONTRACT">{t('employees.contract')}</MenuItem>
                <MenuItem value="INTERN">{t('employees.intern')}</MenuItem>
              </TextField>
              <TextField
                fullWidth
                select
                label="Status"
                value={employee.status}
                onChange={(e) => setEmployee({ ...employee, status: e.target.value })}
                disabled={loading}
              >
                <MenuItem value="ACTIVE">Active</MenuItem>
                <MenuItem value="ON_LEAVE">On Leave</MenuItem>
                <MenuItem value="TERMINATED">Terminated</MenuItem>
              </TextField>
              <TextField
                fullWidth
                type="number"
                label={t('employees.salary')}
                value={employee.salary}
                onChange={(e) => setEmployee({ ...employee, salary: Number(e.target.value) })}
                disabled={loading}
                inputProps={{ min: 0, step: 100 }}
              />
            </Box>

            <Divider />

            {/* Address */}
            <Typography variant="h6" color="text.secondary">
              Address
            </Typography>

            <TextField
              fullWidth
              label="Street Address"
              value={employee.address}
              onChange={(e) => setEmployee({ ...employee, address: e.target.value })}
              disabled={loading}
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label={t('common.city')}
                value={employee.city}
                onChange={(e) => setEmployee({ ...employee, city: e.target.value })}
                disabled={loading}
              />
              <TextField
                fullWidth
                label={t('common.country')}
                value={employee.country}
                onChange={(e) => setEmployee({ ...employee, country: e.target.value })}
                disabled={loading}
              />
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, pt: 1 }}>
              <Button
                type="submit"
                variant="contained"
                startIcon={<Save />}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Employee'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<Cancel />}
                onClick={() => navigate('/employees')}
                disabled={loading}
              >
                Cancel
              </Button>
            </Box>
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
};

export default EmployeeForm;
